import React, { useState, useEffect } from 'react';
import { X, Shield, Mail, MessageCircle } from 'lucide-react';

const AdminAccessDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [action, setAction] = useState('');
  const [onContactAdmin, setOnContactAdmin] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};
      setAction(detail.action || 'perform this action');
      setOnContactAdmin(() => detail.onContactAdmin);
      setIsOpen(true);
    };

    window.addEventListener('app:show-admin-dialog', handler);
    return () => window.removeEventListener('app:show-admin-dialog', handler);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    setAction('');
    setOnContactAdmin(null);
  };

  const handleContactAdmin = () => {
    if (onContactAdmin && typeof onContactAdmin === 'function') {
      onContactAdmin();
    } else {
      // Default action - show notification with contact info
      window.dispatchEvent(new CustomEvent('app:notify', {
        detail: {
          type: 'info',
          message: 'Please contact your system administrator for access.'
        }
      }));
    }
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1001]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <Shield className="w-6 h-6 text-amber-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Admin Access Required
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <p className="text-gray-600 mb-3">
              You need administrator privileges to <strong>{action}</strong>.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Shield className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">Access Restricted</p>
                  <p>This action requires administrator permissions. Please contact your system administrator to:</p>
                  <ul className="mt-2 list-disc list-inside space-y-1">
                    <li>Request the necessary permissions</li>
                    <li>Get assistance with this task</li>
                    <li>Learn about your current access level</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleContactAdmin}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center space-x-2"
          >
            <Mail className="w-4 h-4" />
            <span>Contact Admin</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminAccessDialog;
